import { BrowserFileUtils } from '../utils/BrowserFileUtils'

/**
 * 浏览器兼容的文件写入器
 * 在内存中构建数据，最后导出为 Blob/File
 */
export class BrowserFileWriter {
  private chunks: Uint8Array[] = []
  private _totalLength: number = 0
  private lengthStack: Array<{ placeholder: Uint8Array; contentStart: number }> = []

  constructor() {
    this.chunks = []
    this._totalLength = 0
  }

  /**
   * 写入字符串 (先写入长度，再写入内容)
   */
  public writeString(str: string): void {
    const bytes = BrowserFileUtils.stringToByte(str)
    this.writeInt(bytes.length)
    this.writeBytes(bytes)
  }

  /**
   * 写入固定长度字符串 (不写入长度)
   */
  public writeStringRaw(str: string): void {
    const bytes = BrowserFileUtils.stringToByte(str)
    this.writeBytes(bytes)
  }

  /**
   * 写入固定 6 字节的 MS 字符串
   */
  public writeMSString(str: string): void {
    const bytes = BrowserFileUtils.stringToByte(str)
    const fixed = new Uint8Array(BrowserFileUtils.MSSTRING_LENGTH)
    fixed.set(bytes.subarray(0, BrowserFileUtils.MSSTRING_LENGTH))
    this.writeBytes(fixed)
  }

  /**
   * 写入 int32
   */
  public writeInt(num: number): void {
    const bytes = BrowserFileUtils.int32ToBytes(num)
    this.writeBytes(bytes)
  }

  /**
   * 写入 int64
   */
  public writeInt64(num: number): void {
    const arr = BrowserFileUtils.int64ToArrayNumber(num)
    this.writeBytes(new Uint8Array(arr))
  }

  /**
   * 写入浮点数
   */
  public writeFloat(num: number): void {
    const bytes = BrowserFileUtils.float2Bytes(num, false)
    this.writeBytes(bytes)
  }

  /**
   * 写入双精度浮点数
   */
  public writeDouble(num: number): void {
    const bytes = BrowserFileUtils.float2Bytes(num, true)
    this.writeBytes(bytes)
  }

  /**
   * 写入布尔值
   */
  public writeBool(b: boolean): void {
    const bytes = BrowserFileUtils.bool2Bytes(b)
    this.writeBytes(bytes)
  }

  /**
   * 写入单个字节
   */
  public writeByte(byte: number): void {
    this.writeBytes(new Uint8Array([byte & 0xFF]))
  }

  /**
   * 写入字节数组
   */
  public writeBytes(bytes: Uint8Array | number[]): void {
    const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
    this.chunks.push(data)
    this._totalLength += data.length
  }

  /**
   * 写入 UTF16LE 字符串
   */
  public writeUString(str: string): void {
    const arr = BrowserFileUtils.string2unicode(str)
    this.writeInt(arr.length)
    this.writeBytes(new Uint8Array(arr))
  }

  /**
   * 写入 UTF16LE 字符串 (不写入长度)
   */
  public writeUStringRaw(str: string): void {
    const arr = BrowserFileUtils.string2unicode(str)
    this.writeBytes(new Uint8Array(arr))
  }

  /**
   * 获取总长度
   */
  public get length(): number {
    return this._totalLength
  }

  /**
   * 兼容旧写入器的当前位置字段
   */
  public get start(): number {
    return this._totalLength
  }

  /**
   * 获取所有数据
   */
  public toUint8Array(): Uint8Array {
    const result = new Uint8Array(this._totalLength)
    let offset = 0
    for (const chunk of this.chunks) {
      result.set(chunk, offset)
      offset += chunk.length
    }
    return result
  }

  /**
   * 获取 ArrayBuffer
   */
  public toArrayBuffer(): ArrayBuffer {
    const arr = this.toUint8Array()
    return arr.buffer.slice(arr.byteOffset, arr.byteOffset + arr.byteLength) as ArrayBuffer
  }

  /**
   * 获取 Blob
   */
  public toBlob(mimeType: string = 'application/octet-stream'): Blob {
    return new Blob([this.toArrayBuffer()], { type: mimeType })
  }

  /**
   * 获取 File
   */
  public toFile(fileName: string, mimeType: string = 'application/octet-stream'): File {
    return new File([this.toArrayBuffer()], fileName, { type: mimeType })
  }

  /**
   * 下载文件
   */
  public download(fileName: string, mimeType: string = 'application/octet-stream'): void {
    const blob = this.toBlob(mimeType)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  /**
   * 清空数据
   */
  public clear(): void {
    this.chunks = []
    this._totalLength = 0
    this.lengthStack = []
  }

  /**
   * 关闭写入器
   */
  public close(): void {
    this.clear()
  }

  /**
   * 写入一个 4 字节长度占位，writerLength 时回填其后内容长度。
   */
  public writerIntLength(): void {
    const placeholder = new Uint8Array(BrowserFileUtils.INT_LENGTH)
    this.writeBytes(placeholder)
    this.lengthStack.push({
      placeholder,
      contentStart: this._totalLength,
    })
  }

  /**
   * 回填最近一次 writerIntLength 的内容长度。
   */
  public writerLength(): void {
    const marker = this.lengthStack.pop()
    if (!marker) return
    const contentLength = this._totalLength - marker.contentStart
    marker.placeholder.set(BrowserFileUtils.int32ToBytes(contentLength))
  }
}

export default BrowserFileWriter
